import React, { useContext, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { DrawerHeader } from '@components/drawer-header';
import { GlobalContext } from 'context/userContext';
import { actionLabel, formatAuditTimestamp, summarizeAuditValue } from '@utils/audit-history';
import { AuditEvent, AuditHistoryFilters, listAuditHistory } from './api';

const ADMIN_ROLES = ['admin', 'Automni-superadmin'];

function normalizeFilter(value?: string | null) {
  const trimmed = value?.trim() || '';
  return trimmed || undefined;
}

function EventDetail({ event }: { event: AuditEvent }) {
  const [open, setOpen] = useState(false);

  return (
    <Card elevation={2} sx={{ borderRadius: 3, border: '1px solid rgba(123,16,42,0.12)', overflow: 'hidden' }}>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="h6" fontFamily="Saira" fontWeight={800} color="primary.main">
                  {actionLabel(event.action)}
                </Typography>
                <Chip size="small" label={event.action || 'sem action'} variant="outlined" />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {event.api_cmd || `${event.api_method || 'API'} ${event.api_path || ''}`}
              </Typography>
            </Box>
            <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
              <Typography variant="subtitle2" fontWeight={700}>{formatAuditTimestamp(event.timestamp)}</Typography>
              <Typography variant="caption" color="text.secondary">IP: {event.ip || 'não registrado'}</Typography>
            </Stack>
          </Stack>

          <Grid container spacing={1.5}>
            <Grid item xs={12} md={3}>
              <Chip label={`Usuário: ${event.actor || '<unknown>'}`} sx={{ width: '100%', justifyContent: 'flex-start' }} />
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label={`Tenant: ${event.tenant || '—'}`} sx={{ width: '100%', justifyContent: 'flex-start' }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Chip label={`Payload: ${summarizeAuditValue(event.payload)}`} sx={{ width: '100%', justifyContent: 'flex-start' }} />
            </Grid>
          </Grid>

          <Divider />

          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Antes: {summarizeAuditValue(event.before)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Depois: {summarizeAuditValue(event.after)}
            </Typography>
          </Stack>

          <Box>
            <Button size="small" variant="text" onClick={() => setOpen((value) => !value)}>
              {open ? 'Ocultar JSON completo' : 'Ver JSON completo'}
            </Button>
            <Collapse in={open}>
              <Box
                component="pre"
                sx={{
                  mt: 1,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: '#111827',
                  color: '#e5e7eb',
                  overflowX: 'auto',
                  fontSize: 12,
                }}
              >
                {JSON.stringify(event, null, 2)}
              </Box>
            </Collapse>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function AuditHistoryPage() {
  const { operation, mainUser } = useContext(GlobalContext);
  const [limit, setLimit] = useState(200);
  const [actorFilter, setActorFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [apiFilter, setApiFilter] = useState('');
  const isAdmin = mainUser?.roles?.some((role: string) => ['ADMINISTRADOR', 'SUPERADMINISTRADOR', 'Automni-superadmin'].includes(role));
  const dbReady = !!operation;
  const theme = useTheme();

  const filters = useMemo<AuditHistoryFilters>(() => ({
    actor: normalizeFilter(actorFilter),
    action: normalizeFilter(actionFilter),
    api_cmd: normalizeFilter(apiFilter),
  }), [actorFilter, actionFilter, apiFilter]);

  const auditQuery = useQuery<AuditEvent[]>({
    queryKey: ['audit-history', operation, limit, filters],
    queryFn: () => listAuditHistory(String(operation || ''), limit, filters),
    enabled: Boolean(isAdmin && dbReady),
  });

  const events = useMemo<AuditEvent[]>(() => auditQuery.data || [], [auditQuery.data]);
  const actors = useMemo(() => new Set(events.map((event) => event.actor).filter(Boolean)).size, [events]);
  const actions = useMemo(() => new Set(events.map((event) => event.action).filter(Boolean)).size, [events]);

  return (
    <DrawerHeader title="Histórico de Modificações">
      <Stack spacing={3} sx={{ maxWidth: 1320, mx: 'auto' }}>
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2.5, md: 3 },
            bgcolor: 'background.paper',
            borderLeft: `6px solid ${theme.palette.primary.main}`,
            borderRadius: 2,
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h4" fontFamily="Saira" fontWeight={900} color="text.primary">Histórico de Modificações</Typography>
              <Typography variant="body1" color="text.secondary">
                Visual Command Interface para rastrear quem alterou configurações, quando alterou, e qual comando de API gravou a mudança.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip label={`${events.length} eventos`} color="primary" variant="outlined" />
              <Chip label={`${actors} usuários`} color="primary" variant="outlined" />
              <Chip label={`${actions} ações`} color="primary" variant="outlined" />
            </Stack>
          </Stack>
        </Paper>

        {!isAdmin ? (
          <Alert severity="warning">Histórico visível apenas para administradores.</Alert>
        ) : !dbReady ? (
          <Alert severity="warning">Selecione uma operação para carregar o histórico de modificações.</Alert>
        ) : (
          <>
            <Card elevation={1} sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack spacing={2.5}>
                  <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between" gap={2}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={800}>Operação: {operation}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Fonte: coleção audit_events — registros mais recentes primeiro, com filtros diretos por usuário, ação e comando.
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        type="number"
                        label="Limite"
                        value={limit}
                        inputProps={{ min: 1, max: 500 }}
                        onChange={(event) => setLimit(Math.max(1, Math.min(Number(event.target.value) || 200, 500)))}
                        sx={{ width: 130 }}
                      />
                      <Button variant="contained" onClick={() => auditQuery.refetch()} disabled={auditQuery.isFetching}>
                        Atualizar
                      </Button>
                    </Stack>
                  </Stack>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Filtrar por usuário"
                        placeholder="ex: henrique@automni..."
                        value={actorFilter}
                        onChange={(event) => setActorFilter(event.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Filtrar por action"
                        placeholder="ex: equipaments.save"
                        value={actionFilter}
                        onChange={(event) => setActionFilter(event.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Filtrar por comando/API"
                        placeholder="ex: POST /equipaments..."
                        value={apiFilter}
                        onChange={(event) => setApiFilter(event.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </CardContent>
            </Card>

            {auditQuery.isLoading ? (
              <Stack alignItems="center" mt={4}>
                <CircularProgress />
                <Typography variant="body2" mt={2}>Carregando histórico...</Typography>
              </Stack>
            ) : auditQuery.isError ? (
              <Alert severity="error">Erro ao carregar histórico de modificações.</Alert>
            ) : events.length === 0 ? (
              <Alert severity="info">Nenhum evento de modificação encontrado para esta operação.</Alert>
            ) : (
              <Stack spacing={2}>
                {events.map((event, index) => (
                  <EventDetail key={event._id || `${event.timestamp}-${index}`} event={event} />
                ))}
              </Stack>
            )}
          </>
        )}
      </Stack>
    </DrawerHeader>
  );
}
